/**
 * Reports Dashboard
 * Dashboard med kort-layout för rapporter
 */

import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { useTranslation } from '@/store/languageStore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/store/authStore';
import {
  MapIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  BoltIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  CloudIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  KeyIcon,
  ChartBarSquareIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  BeakerIcon,
  CurrencyDollarIcon,
  PresentationChartLineIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const Reports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasFeatureAccess } = usePermissions();

  const isAdmin = user?.is_global_admin === true;

  // Kontrollera om användaren har tillgång till ett feature.
  // hasFeatureAccess innehåller redan paketkontrollen internt och returnerar false
  // när permissions inte är laddade. hasPackageAccess används INTE här — den returnerar
  // true när packageFeatures är null (inte laddade), vilket ger falska positiver.
  const canSee = (...featureCodes: string[]) =>
    isAdmin || featureCodes.some((code) => hasFeatureAccess(code));

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(ROUTES.SYSTEM_REPORTS)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <ChartBarIcon className="w-6 h-6 text-primary" />
          {t('reports.title') || 'Rapporter'}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        {canSee('REPORTS_TRIPS', 'REPORT_TRIPS') && (
          <Link
            to={ROUTES.REPORTS_TRIPS}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <MapIcon className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('trips.title') || 'Trippar'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('trips.description') || 'Visa och analysera trippar för dina fordon'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_ECO_DRIVING', 'REPORT_ECO_DRIVING') && (
          <Link
            to={ROUTES.REPORTS_ECO_DRIVING}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <ChartBarIcon className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('eco.title') || 'ECO Driving'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('eco.description') || 'Analysera körvanor och miljöpåverkan'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_SPEED_COMPLIANCE', 'REPORT_SPEED_COMPLIANCE') && (
          <Link
            to={ROUTES.REPORTS_SPEED_COMPLIANCE}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <ShieldCheckIcon className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('speedCompliance.title') || 'Hastighetsefterlevnad'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('speedCompliance.description') || 'Analysera hastighetsefterlevnad per organisation och fordon'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_ENERGY_CONSUMPTION', 'REPORT_ENERGY') && (
          <Link
            to={ROUTES.REPORTS_ENERGY_CONSUMPTION}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <BoltIcon className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.energyConsumption.title') || 'Energiförbrukning'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.energyConsumption.description') || 'Analysera bränsleförbrukning per organisation och fordon'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_VEHICLE_WEAR', 'REPORT_VEHICLE_WEAR') && (
          <Link
            to={ROUTES.REPORTS_VEHICLE_WEAR}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <WrenchScrewdriverIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.vehicleWear.title') || 'Fordonsslitage'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.vehicleWear.description') || 'Analysera hur körbeteende påverkar fordonsslitage'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_UNDERUTILIZED', 'REPORT_UNDERUTILIZED') && (
          <Link
            to={ROUTES.REPORTS_UNDERUTILIZED}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <TruckIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.underutilized.title') || 'Underutnyttjade fordon'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.underutilized.description') || 'Identifiera fordon med lågt utnyttjande'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {(canSee('REPORTS_CO2') || canSee('REPORT_CO2')) && (
          <Link
            to={ROUTES.REPORTS_CO2}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-sky-100 dark:bg-sky-900/30 rounded-lg">
                <CloudIcon className="w-8 h-8 text-sky-600 dark:text-sky-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.co2.title') || 'CO₂-utsläpp'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.co2.description') || 'Beräkna och analysera CO₂-utsläpp'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_GEOFENCE_ZONES', 'REPORT_GEOFENCE_ZONES') && (
          <Link
            to={ROUTES.REPORTS_GEOFENCE_ZONES}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                <MapPinIcon className="w-8 h-8 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.geofenceZone.title') || 'Geofence Zonrapport'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.geofenceZone.description') || 'Analysera hur ofta fordon befinner sig inom zoner'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {(canSee('REPORTS_DRIVING_LOG') || canSee('DRIVING_LOG')) && (
          <Link
            to={ROUTES.REPORTS_DRIVING_LOG}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <ClipboardDocumentListIcon className="w-8 h-8 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.drivingLog.title') || 'Körjournal'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.drivingLog.description') || 'Körjournal enligt Skatteverkets riktlinjer'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_ODOMETER', 'REPORT_ODOMETER') && (
          <Link
            to={ROUTES.REPORTS_ODOMETER}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <ClockIcon className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.odometerReport.title') || 'Mätarställningsrapport'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.odometerReport.description') || 'Visa mätarställning per fordon vid specifikt datum'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_VEHICLE_KPI') && (
          <Link
            to={ROUTES.REPORTS_VEHICLE_KPI}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                <ChartBarSquareIcon className="w-8 h-8 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.vehicleKpi.title') || 'Nyckeltal fordon'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.vehicleKpi.description') || 'Sammanställning av nyckeltal per fordon, månad och år'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_DTC') && (
          <Link
            to={ROUTES.REPORTS_DTC}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.dtc.title') || 'DTC-felrapport'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.dtc.description') || 'Visa och analysera diagnostiska felkoder (DTC) per fordon'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_BOOKING_STATS') && (
          <Link
            to={ROUTES.REPORTS_BOOKING_STATS}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <CalendarDaysIcon className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.bookingStats.title')}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.bookingStats.description')}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORT_RENTAL') && (
          <Link
            to={ROUTES.REPORTS_RENTAL}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                <KeyIcon className="w-8 h-8 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('rentalReport.title') || 'Uthyrningsrapport'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('rentalReport.description') || 'Komplett rapport för uthyrning med resor, kostnader och status'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_FUEL_ANALYSIS') && (
          <Link
            to={ROUTES.REPORTS_FUEL_ANALYSIS}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <BeakerIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.fuelAnalysis.title') || 'Bränsleanalys'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.fuelAnalysis.description') || 'Analysera bränslekostnader och förbrukning från importerade transaktioner'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_FLEET_TCO') && (
          <Link
            to={ROUTES.REPORTS_FLEET_TCO}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <CurrencyDollarIcon className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.fleetTco.title') || 'Flottkostnadsanalys (TCO)'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.fleetTco.description') || 'Total ägandekostnad per fordon: bränsle, service, leasing och försäkring'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('REPORTS_EXECUTIVE') && (
          <Link
            to={ROUTES.REPORTS_EXECUTIVE}
            className="p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <PresentationChartLineIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('reports.executive.title') || 'Ledningsrapport'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('reports.executive.description') || 'Övergripande KPI:er för hela flottan med kostnader, utnyttjande och CO₂'}
                </p>
              </div>
            </div>
          </Link>
        )}

      </div>
    </div>
  );
};

export default Reports;
